import React, { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import {
  findCurrentShow,
  findNextShow,
  formatShowLocalTime,
  showStartInWeek,
} from "../lib/schedule-time";
import { Mic2, Radio, Calendar, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const OnAirNowBanner = () => {
  const [shows, setShows] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/shows`);
        if (res.ok && alive) setShows(await res.json());
      } catch {}
    };
    load();
    const poll = setInterval(load, 60000);
    const tickId = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      alive = false;
      clearInterval(poll);
      clearInterval(tickId);
    };
  }, []);

  const now = DateTime.now();
  const currentShow = useMemo(() => findCurrentShow(shows, now), [shows, tick, now.toMillis()]);
  const next = useMemo(() => findNextShow(shows, now), [shows, tick, now.toMillis()]);

  if (shows.length === 0) return null;

  return (
    <section
      data-testid="on-air-banner"
      className="max-w-7xl mx-auto px-5 md:px-10 mt-4"
    >
      {currentShow ? (
        <div className="rounded-lg border border-crimson/50 bg-gradient-to-r from-crimson/25 via-jet-surface to-jet-surface p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="relative shrink-0 w-14 h-14 grid place-items-center rounded-full bg-crimson/20 border border-crimson/50">
              <Mic2 className="w-6 h-6 text-crimson-live" />
              <span className="absolute inset-0 rounded-full border border-crimson-live live-ping" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="relative flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
                  <span className="relative inline-flex w-2 h-2 rounded-full bg-crimson-live live-dot" />
                </span>
                <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-crimson-live">
                  On Air Now
                </span>
              </div>
              <div className="font-display text-2xl md:text-3xl uppercase text-white leading-tight truncate">
                {currentShow.name}
              </div>
              <div className="font-body text-sm text-neutral-300 truncate">
                {currentShow.dj ? `With ${currentShow.dj} · ` : ""}
                {formatShowLocalTime(currentShow)}
              </div>
            </div>
          </div>
          <Link
            to="/schedule"
            data-testid="on-air-view-schedule"
            className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors self-start md:self-auto"
          >
            <Calendar className="w-4 h-4" />
            Full Schedule
          </Link>
        </div>
      ) : next ? (
        <div className="rounded-lg border border-gold/25 bg-jet-surface/70 p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="shrink-0 w-14 h-14 grid place-items-center rounded-full bg-gold/15 border border-gold/40">
              <Radio className="w-6 h-6 text-gold" />
            </div>
            <div className="min-w-0">
              <div className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold mb-1">
                Up Next
              </div>
              <div className="font-display text-2xl md:text-3xl uppercase text-white leading-tight truncate">
                {next.show.name}
              </div>
              <div className="font-body text-sm text-neutral-300 truncate">
                {next.show.dj ? `With ${next.show.dj} · ` : ""}
                {next.startsAt.setZone(DateTime.local().zoneName).toLocaleString({
                  weekday: "short",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </div>
            </div>
          </div>
          <Link
            to="/schedule"
            className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors self-start md:self-auto"
          >
            <ArrowRight className="w-4 h-4" />
            Full Schedule
          </Link>
        </div>
      ) : null}
    </section>
  );
};

export default OnAirNowBanner;
